import { motion } from "framer-motion";
import { useEffect } from "react";
import { useScene } from "./scene/SceneController";

const brandName = process.env.REACT_APP_BRAND_NAME;
const workflow = [
    {
        step: "01",
        title: "Documents Arrive",
        description: "Invoices, receipts and client files flow into one organized workspace.",
    },
    {
        step: "02",
        title: `${brandName} Assists`,
        description: "The system extracts information, identifies patterns and prepares suggestions.",
    },
    {
        step: "03",
        title: "Accountant Approves",
        description: "Your team reviews decisions and keeps complete professional control.",
    },
];

const ControlSection = () => {
    const { updateScene } = useScene();

    useEffect(() => {
        updateScene({
        section: "control",
        chaos: 0.5,
        organization: 0.5,
        coreIntensity: 1.8,
        cameraDistance: 9,
        });
    }, [updateScene]);

    return (
        <section
            id="control"
            className="tw-relative tw-min-h-screen tw-pt-28 tw-pb-16 tw-overflow-hidden"
        >
            <div
                className="
                    tw-absolute
                    tw-inset-0
                    tw-bg-gradient-to-b
                    tw-from-[#050816]
                    tw-via-[#0b1b3a]
                    tw-to-[#020617]
                "
            />

            <div className="tw-relative tw-z-10 tw-max-w-6xl tw-mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="tw-text-center tw-max-w-4xl tw-mx-auto"
                >
                    <p
                        className="
                            tw-text-blue-400
                            tw-uppercase
                            tw-tracking-[0.3em]
                            tw-text-sm
                            tw-mb-5
                        "
                    >
                        You stay in control
                    </p>

                    <h2
                        className="
                            tw-text-4xl
                            md:tw-text-6xl
                            tw-font-bold
                            tw-text-white
                        "
                    >
                        Automation does not replace
                        <br />
                        <span
                            className="
                                tw-bg-gradient-to-r
                                tw-from-blue-400
                                tw-to-cyan-400
                                tw-bg-clip-text
                                tw-text-transparent
                            "
                        >
                            professional judgement.
                        </span>
                    </h2>

                    <p
                        className="
                            tw-mt-6
                            tw-text-lg
                            tw-text-gray-300
                            tw-leading-relaxed
                        "
                    >
                        {brandName} handles the repetitive workload, but every important decision stays with your accountants. Your expertise becomes more valuable, not less.
                    </p>
                </motion.div>

                {/* Workflow */}
                <div className="tw-mt-20 tw-grid md:tw-grid-cols-3 tw-gap-8">
                    {workflow.map((item, index) => (
                        <motion.div
                            key={item.step}
                            initial={{
                                opacity: 0,
                                y: 50,
                            }}
                            whileInView={{
                                opacity: 1,
                                y: 0,
                            }}
                            viewport={{
                                once: true,
                            }}
                            transition={{
                                delay: index * 0.2,
                                duration: 0.7,
                            }}
                            className="
                                tw-relative
                                tw-rounded-3xl
                                tw-p-8
                                tw-bg-white/5
                                tw-backdrop-blur-xl
                                tw-border
                                tw-border-white/10
                            "
                        >
                            {/* Step number */}
                            <div
                                className="
                                    tw-text-blue-400
                                    tw-text-sm
                                    tw-font-bold
                                    tw-mb-6
                                "
                            >
                                {item.step}
                            </div>

                            <h3
                                className="
                                    tw-text-xl
                                    tw-text-white
                                    tw-font-semibold
                                    tw-mb-4
                                "
                            >
                                {item.title}
                            </h3>

                            <p
                                className="
                                    tw-text-gray-400
                                    tw-leading-relaxed
                                "
                            >
                                {item.description}
                            </p>

                            {/* Connector */}

                            {index !== workflow.length - 1 && (
                                <motion.div
                                    initial={{
                                        scaleX: 0,
                                    }}
                                    whileInView={{
                                        scaleX: 1,
                                    }}
                                    viewport={{
                                        once: true,
                                    }}
                                    transition={{
                                        delay: index * 0.3 + 0.5,
                                        duration: 1,
                                    }}
                                    className="
                                        tw-hidden
                                        md:tw-block
                                        tw-absolute
                                        tw-top-1/2
                                        -tw-right-8
                                        tw-w-16
                                        tw-h-[2px]
                                        tw-origin-left
                                        tw-bg-gradient-to-r
                                        tw-from-blue-400
                                        tw-to-cyan-400
                                    "
                                />
                            )}
                        </motion.div>
                    ))}
                </div>

                {/* Final reassurance */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="
                        tw-mt-20
                        tw-max-w-3xl
                        tw-mx-auto
                        tw-rounded-3xl
                        tw-p-10
                        tw-text-center
                        tw-bg-gradient-to-r
                        tw-from-blue-500/10
                        tw-to-cyan-500/10
                        tw-border
                        tw-border-blue-400/20
                    "
                >
                    <h3 className="tw-text-3xl tw-font-bold tw-text-white">
                        Your team becomes accountants again.
                    </h3>

                    <p className="tw-mt-4 tw-text-gray-300 tw-text-lg">
                        Less copying. Less chasing. Less manual processing. More analysis.
                        More advisory. More growth.
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default ControlSection;